import { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown, MapPin, Clock, Share2, Check } from 'lucide-react';
import { SALADES_PHOTOS } from '../data/photos';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Données statiques (fallback si API inaccessible) ──────────────────────────
const STATIC_STEPS = [
  {
    label: "2 Bases",
    sub: "Choisissez parmi 15 bases",
    products: [
      { name: "Lentilles chaudes", hot: true },
      { name: "Batavia", hot: false },
      { name: "Roquette", hot: false },
      { name: "Quinoa", hot: false },
      { name: "Taboulé", hot: false },
      { name: "Pommes de terre vapeur", hot: true },
      { name: "Mâche", hot: false },
      { name: "Pousses d'épinard", hot: false },
      { name: "Lentilles", hot: false },
      { name: "Perles de blé & ciboulette", hot: false },
      { name: "Poêlée de légumes", hot: true },
      { name: "Iceberg", hot: false },
      { name: "Mesclun", hot: false },
      { name: "Pâtes - Mini Penne", hot: false },
      { name: "Riz vinaigré", hot: false },
    ],
  },
  {
    label: "4 Ingrédients",
    sub: "60 ingrédients au choix",
    categories: [
      {
        name: "Légumes & Fruits",
        color: "#80a890",
        items: [
          { name: "Betterave" }, { name: "Concombre" }, { name: "Carottes râpées" },
          { name: "Maïs" }, { name: "Champignons" }, { name: "Tomates quartiers" },
          { name: "Edamames" }, { name: "Olives Kalamata" }, { name: "Chou rouge râpé" },
          { name: "Oignons rouges" }, { name: "Courgettes râpées" }, { name: "Poivrons" },
          { name: "Tomates cerises" }, { name: "Brocolis" }, { name: "Avocat" },
          { name: "Pomme" }, { name: "Ananas" }, { name: "Mangue" },
          { name: "Tartare de tomates" }, { name: "Tartare de concombres" },
          { name: "Poivrons marinés" }, { name: "Houmous" },
          { name: "Aubergines marinées" }, { name: "Tomates cerises confites" },
          { name: "Chou-fleur rôti au curry", hot: true },
          { name: "Patates douces rôties", hot: true },
          { name: "Oignons caramélisés", hot: true },
          { name: "Pommes de terre rôties", hot: true },
          { name: "Champignons chauds", hot: true },
          { name: "Carottes rôties", hot: true },
          { name: "Courgettes rôties", hot: true },
          { name: "Gyoza légumes", hot: true },
          { name: "Falafels", hot: true },
        ],
      },
      {
        name: "Protéines",
        color: "#e86026",
        items: [
          { name: "Lardons", hot: true }, { name: "Cochon confit", hot: true },
          { name: "Saucisses fumées", hot: true }, { name: "Poulet miel/citron", hot: true },
          { name: "Poulet épicé", hot: true }, { name: "Poulet nature chaud", hot: true },
          { name: "Poulet basilic/Grana padano", hot: true },
          { name: "Poulet curry/coco", hot: true }, { name: "Poulet soja/sésame", hot: true },
          { name: "Jambon cru" }, { name: "Jambon blanc" }, { name: "Bacon" },
          { name: "Poulet froid" }, { name: "Œuf dur" }, { name: "Saumon" },
          { name: "Crevettes" }, { name: "Thon" },
        ],
      },
      {
        name: "Fromages",
        color: "#EEB52D",
        items: [
          { name: "Burratina 50g" }, { name: "Grana padano" }, { name: "Mozzarella" },
          { name: "Emmental" }, { name: "Feta" }, { name: "Mimolette" },
          { name: "Fourme d'Ambert" }, { name: "Chèvre" },
        ],
      },
    ],
  },
  {
    label: "2 Condiments",
    sub: "12 condiments au choix",
    products: [
      { name: "Basilic" }, { name: "Coriandre" }, { name: "Ciboulette" },
      { name: "Mélange de graines" }, { name: "Graines de sésame" }, { name: "Noix" },
      { name: "Croûtons" }, { name: "Oignons frits" }, { name: "Cornichons" },
      { name: "Pickles d'oignon rouge" }, { name: "Raisins secs" }, { name: "Grenade" },
    ],
  },
  {
    label: "1 Sauce",
    sub: "parmi 15 sauces",
    products: [
      { name: "Balsamique" }, { name: "Ancienne" }, { name: "Pesto" },
      { name: "Citronnée" }, { name: "Épicée" }, { name: "Mangue & Passion" },
      { name: "Sésame" }, { name: "Traditionnelle" }, { name: "Classique" },
      { name: "Caesar" }, { name: "Andalouse" }, { name: "Nordique" },
      { name: "Huile d'olive" }, { name: "Jus de citron" }, { name: "Miel Moutarde" },
    ],
  },
];

// ── Transformation données API → format STEPS ─────────────────────────────────
function buildSaladesSteps(items) {
  if (!items || items.length === 0) return null;

  const byCategory = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const bases = byCategory.bases || [];
  const ingredients = byCategory.ingredients || [];
  const condiments = byCategory.condiments || [];
  const sauces = byCategory.sauces || [];

  if (!bases.length && !sauces.length) return null;

  const ingredientGroups = [
    { subkeys: ['froid', 'chaud'], name: 'Légumes & Fruits', color: '#80a890' },
    { subkeys: ['proteine_froide', 'proteine_chaude'], name: 'Protéines', color: '#e86026' },
    { subkeys: ['fromage'], name: 'Fromages', color: '#EEB52D' },
  ]
    .map(grp => ({
      name: grp.name,
      color: grp.color,
      items: ingredients
        .filter(i => grp.subkeys.includes(i.subcategory))
        .map(i => ({
          name: i.name,
          hot: i.subcategory === 'chaud' || i.subcategory === 'proteine_chaude',
        })),
    }))
    .filter(c => c.items.length > 0);

  return [
    {
      label: '2 Bases',
      sub: `Choisissez parmi ${bases.length} base${bases.length > 1 ? 's' : ''}`,
      products: bases.map(i => ({ name: i.name, hot: i.subcategory === 'chaud' })),
    },
    {
      label: '4 Ingrédients',
      sub: `${ingredients.length} ingrédients au choix`,
      categories: ingredientGroups.length > 0 ? ingredientGroups : undefined,
      products: ingredientGroups.length === 0 ? ingredients.map(i => ({ name: i.name })) : undefined,
    },
    {
      label: '2 Condiments',
      sub: `${condiments.length} condiment${condiments.length > 1 ? 's' : ''} au choix`,
      products: condiments.map(i => ({ name: i.name })),
    },
    {
      label: '1 Sauce',
      sub: `parmi ${sauces.length} sauce${sauces.length > 1 ? 's' : ''}`,
      products: sauces.map(i => ({ name: i.name })),
    },
  ];
}

export default function MenuSalades() {
  const [steps, setSteps] = useState(STATIC_STEPS);
  const [active, setActive] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [shareState, setShareState] = useState('idle');
  const heroPhotos = useMemo(() => [...SALADES_PHOTOS].sort(() => Math.random() - 0.5).slice(0, 3), []);

  useEffect(() => {
    fetch(`${API}/api/menu/salades`)
      .then(r => r.ok ? r.json() : [])
      .then(items => {
        const built = buildSaladesSteps(items);
        if (built) setSteps(built);
      })
      .catch(() => {}); // fallback silencieux sur les données statiques
  }, []);

  const handleShare = async () => {
    const url = window.location.origin + '/menus-salades';
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Menu Bar à Salade — Grainette Paris 9e',
          text: 'Composez votre salade parmi plus de 80 produits frais. Bar à salade healthy à Paris 9e.',
          url,
        });
      } catch (e) { /* annulé par l'utilisateur */ }
    } else {
      await navigator.clipboard.writeText(url);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2200);
    }
  };

  const SALADE_LINKS = [
    {
      label: "Grainette Salades - Mogador",
      address: "27 rue de Mogador, 75009",
      hours: "Lun – Ven · 11h30 – 15h00",
      href: "https://clicks.tastycloud.fr/f8def329f79cc069cc86029fd12781cb49a501fadf230be5f094e9aeaf656e66/menu/73742",
    },
    {
      label: "Grainette Salades - Godot de Mauroy",
      address: "16 rue Godot de Mauroy, 75009",
      hours: "Lun – Ven · 11h30 – 15h00",
      href: "https://commandes.grainette.fr/home/1064/2/menu",
    },
  ];

  return (
    <div className="bg-[#FAFAFA] min-h-screen pt-16" data-testid="menu-salades-page">

      {/* HEADER — 3 photos côte à côte */}
      <div className="relative" style={{ minHeight: '440px' }} data-testid="salades-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 flex">
            {heroPhotos.map((photo, i) => (
              <div
                key={i}
                className="flex-1 h-full"
                style={{
                  backgroundImage: `url(${photo})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            ))}
          </div>
          <div className="absolute inset-0" style={{ backgroundColor: '#2A3630', opacity: 0.75 }} />
        </div>
        <div className="relative flex items-center justify-center py-24 px-4" style={{ zIndex: 2 }}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <img
              src="https://customer-assets.emergentagent.com/job_grainette-migration/artifacts/i2pxliea__Grainette%20_%20.png"
              alt="Grainette"
              className="w-auto object-contain mx-auto mb-4"
              style={{ height: '11rem', filter: 'brightness(0) invert(1)' }}
            />
            <h1 className="text-5xl sm:text-6xl font-black mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Bar à Salade
            </h1>
            <p className="text-white/90 text-base max-w-xl mx-auto mb-6">
              Plus de 80 produits frais pour composer la salade qui vous ressemble
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="relative inline-block">
                <button
                  onClick={() => setOrderOpen(!orderOpen)}
                  data-testid="order-salad-btn"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-white font-bold rounded-full text-sm btn-lift"
                  style={{ color: '#80a890' }}
                >
                  Commander en ligne
                  <ChevronDown size={14} className={`transition-transform duration-200 ${orderOpen ? 'rotate-180' : ''}`} />
                </button>
                {orderOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    {SALADE_LINKS.map((link, i) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block px-5 py-4 hover:bg-sage/10 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''}`}
                        onClick={() => setOrderOpen(false)}
                      >
                        <p className="text-sm font-black mb-2" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
                          <span style={{ color: '#80a890' }}>Grainette</span>{link.label.replace('Grainette', '')}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-1">
                          <MapPin size={11} style={{ color: '#80a890', flexShrink: 0 }} />{link.address}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <Clock size={11} style={{ color: '#80a890', flexShrink: 0 }} />{link.hours}
                        </p>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleShare}
                data-testid="share-salades-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-200"
                style={{
                  backgroundColor: shareState === 'copied' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {shareState === 'copied'
                  ? <><Check size={14} /> Lien copié !</>
                  : <><Share2 size={14} /> Partager</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Panneau menu physique */}
      <section className="py-14 px-6 bg-[#F5F5F5]" data-testid="menus-section">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-md border border-gray-100">
            <img
              src="/menu-boards/entree-web.jpg"
              alt="Panneau menu Grainette — compositions et suppléments"
              className="w-full h-auto"
              loading="lazy"
              decoding="async"
              data-testid="entree-board-img"
            />
          </div>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="py-14 px-6 bg-[#F5F5F5]" data-testid="concept-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-black"
              style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
              Comment composer votre salade ?
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-0">
            {steps.map((step, i) => (
              <div key={step.label} className="flex flex-col sm:flex-row items-center">
                <div
                  onClick={() => setActive(active === i ? null : i)}
                  data-testid={`concept-step-${i + 1}`}
                  className="flex flex-col items-center text-center px-4 py-5 rounded-2xl border border-gray-200 cursor-pointer select-none"
                  style={{
                    backgroundColor: active === i ? '#80a890' : 'white',
                    width: '160px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                    transform: active === i ? 'scale(1.08)' : 'scale(1)',
                    boxShadow: active === i ? '0 8px 24px rgba(128,168,144,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                  onMouseEnter={e => { if (active !== i) { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.1)'; }}}
                  onMouseLeave={e => { if (active !== i) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}}
                >
                  <p className="font-black text-sm mb-0.5"
                    style={{ fontFamily: 'Barlow, sans-serif', color: active === i ? 'white' : '#2A3630' }}>
                    {step.label}
                  </p>
                  <p className="text-xs" style={{ color: active === i ? 'rgba(255,255,255,0.8)' : '#9ca3af', fontFamily: 'Poppins, sans-serif' }}>
                    {step.sub}
                  </p>
                </div>

                {i < steps.length - 1 && (
                  <svg viewBox="0 0 80 80" width="32" height="32" className="shrink-0 mx-0.5 my-1 sm:my-0">
                    <defs>
                      <filter id={`rough-${i}`} x="-15%" y="-15%" width="130%" height="130%">
                        <feTurbulence type="fractalNoise" baseFrequency="0.07" numOctaves="4" seed={i + 2} result="noise"/>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G"/>
                      </filter>
                    </defs>
                    <path d="M29,4 H51 V29 H76 V51 H51 V76 H29 V51 H4 V29 H29 Z"
                      fill="#80a890" filter={`url(#rough-${i})`}/>
                  </svg>
                )}
              </div>
            ))}
          </div>

          {/* Panneau détail */}
          {active !== null && (steps[active].products?.length > 0 || steps[active].categories) && (
            <div className="mt-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
              style={{ animation: 'fadeIn 0.2s ease' }}
              data-testid="concept-detail-panel">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-2xl font-black" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
                    {steps[active].label}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {steps[active].sub}
                  </p>
                </div>
                <button onClick={() => setActive(null)} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
                  <X size={20} />
                </button>
              </div>

              {steps[active].products && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {steps[active].products.map((p) => (
                    <div key={p.name} className="relative px-3 py-2 rounded-xl text-center text-sm font-medium"
                      style={{ backgroundColor: 'rgba(128,168,144,0.1)', color: '#2A3630', fontFamily: 'Poppins, sans-serif' }}>
                      {p.hot && (
                        <span className="absolute -top-2 -right-2 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                          style={{ backgroundColor: '#e86026' }}>
                          chaud
                        </span>
                      )}
                      {p.name}
                    </div>
                  ))}
                </div>
              )}

              {steps[active].categories && (
                <div className="space-y-6">
                  {steps[active].categories.map((cat) => (
                    <div key={cat.name}>
                      <h4 className="font-black text-sm mb-3 uppercase tracking-wider"
                        style={{ fontFamily: 'Barlow, sans-serif', color: cat.color }}>
                        {cat.name}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        {cat.items.map((item) => (
                          <div key={item.name} className="relative px-3 py-2 rounded-xl text-center text-xs font-medium"
                            style={{ backgroundColor: `${cat.color}18`, color: '#2A3630', fontFamily: 'Poppins, sans-serif' }}>
                            {item.hot && (
                              <span className="absolute -top-2 -right-1 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                                style={{ backgroundColor: '#e86026' }}>
                                chaud
                              </span>
                            )}
                            {item.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Commander */}
      <section className="py-16 px-6 bg-[#F5F5F5] border-t border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-3" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
            Envie d'une salade ?
          </h2>
          <p className="text-gray-500 text-sm mb-8">Commandez en ligne pour récupérer votre salade sans attendre.</p>
          <div className="relative inline-block">
            <button
              onClick={() => setCtaOpen(!ctaOpen)}
              data-testid="cta-order-salad-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm btn-lift text-white"
              style={{ backgroundColor: '#80a890' }}
            >
              Commander en ligne
              <ChevronDown size={14} className={`transition-transform duration-200 ${ctaOpen ? 'rotate-180' : ''}`} />
            </button>
            {ctaOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {SALADE_LINKS.map((link, i) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block px-5 py-4 hover:bg-sage/10 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''}`}
                    onClick={() => setCtaOpen(false)}
                  >
                    <p className="text-sm font-black mb-2 text-left" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
                      <span style={{ color: '#80a890' }}>Grainette</span>{link.label.replace('Grainette', '')}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-1">
                      <MapPin size={11} style={{ color: '#80a890', flexShrink: 0 }} />{link.address}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Clock size={11} style={{ color: '#80a890', flexShrink: 0 }} />{link.hours}
                    </p>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

    </div>
  );
}
